import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Subject } from 'rxjs';

import { getCount } from '../api';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { emptyObject } from 'ui-components/default-props/empty';

import { usePusherEvent } from '../../../hooks/use-pusher-event';
import { BATCH_PROGRESS, STORE_REFRESH_STATUS } from '../../../services/pusher-service/pusher-contract-messages';
import Global from '../../../store/global-selectors';
import { MarketplaceIds } from '../../../enums/marketplace-ids';

import { messages } from '../components/onboard-section/messages';
import { useModifiableSellerSetting } from '../../../hooks/use-modifiable-seller-setting';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { LABEL_SERVICE_BATCH_STATUS_IDS } from '../../../constants/label-batch-status-ids';

export const orderCountSubject = new Subject();

export const useOrderImportData = (isPopup) => {
  const [onboardingJson, setOnboardingJson] = useModifiableSellerSetting(SellerSettings.sellerOnboardingJson, emptyObject);
  const { hasPrintedLabels = false, hasCreatedOrders = false } = onboardingJson;

  // Marketplace data
  const allSellerStores = useSelector(Global.selectSellerStores);

  // Order Data
  const getOrdersCount = useCallback(async () => {
    // We don't need to fetch counts if we have already created orders
    if (hasCreatedOrders || !isPopup) {
      return;
    }
    try {
      const response = await getCount();
      const countByStatus = response.data.statusListByStore.map((statusInfo) => statusInfo.count);
      const hasOrders = countByStatus.reduce((acc, count) => acc + count, 0) > 0;
      if (hasOrders) {
        setOnboardingJson((prevValue) => ({
          ...prevValue,
          hasCreatedOrders: true,
        }));
      }
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.fetchErrorNotificationTitle} />,
        content: <FormattedMessage {...messages.fetchErrorNotificationContent} />,
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    }
  }, [hasCreatedOrders, isPopup, setOnboardingJson]);

  useEffect(() => {
    orderCountSubject.subscribe((entities) => {
      if (entities.salesOrders && !hasCreatedOrders && isPopup) {
        setOnboardingJson((prevValue) => ({
          ...prevValue,
          hasCreatedOrders: true,
        }));
      }
    });
  }, [hasCreatedOrders, isPopup, setOnboardingJson]);

  usePusherEvent(STORE_REFRESH_STATUS, getOrdersCount);

  // Label Purchases
  // Update labelsPrinted in onboardingJson if it has increased
  const markLabelPrinted = useCallback(
    (pusherMessage) => {
      if (hasPrintedLabels || !isPopup) {
        return;
      }
      const { status = {} } = pusherMessage;
      const { StatusID: statusId, Completed: newLabelsPrintedCount } = status;
      if (statusId !== LABEL_SERVICE_BATCH_STATUS_IDS.COMPLETED) {
        return;
      }
      // Only need to know if we need to pluralize the label count
      if (newLabelsPrintedCount > 0) {
        setOnboardingJson((prevValue) => ({
          ...prevValue,
          hasPrintedLabels: true,
        }));
      }
    },
    [hasPrintedLabels, isPopup, setOnboardingJson],
  );
  usePusherEvent(BATCH_PROGRESS, markLabelPrinted);

  return useMemo(() => {
    const sellerStoresToRender = allSellerStores.filter((store) => store.isActive && store.marketplaceId !== MarketplaceIds.ShipStation);
    const _hasConnectedStore = sellerStoresToRender.length > 0;
    return {
      isOrderImportComplete: !!sellerStoresToRender.length || hasCreatedOrders || hasPrintedLabels,
      hasConnectedStore: _hasConnectedStore,
      sellerStoreToRender: _hasConnectedStore ? sellerStoresToRender[0] : emptyObject,
    };
  }, [allSellerStores, hasCreatedOrders, hasPrintedLabels]);
};
